<template>
  <div class="min-h-screen bg-slate-900 flex flex-col">

    <!-- Navbar -->
    <nav class="bg-slate-800/80 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-lg">
          <i class="fa-solid fa-laptop-code text-white text-sm"></i>
        </div>
        <span class="text-white font-black text-lg">
          MADAV<span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">Solutions</span>
        </span>
      </div>
      <div class="flex items-center gap-3">
        <span class="text-slate-400 text-sm hidden sm:block">
          <i class="fa-solid fa-user mr-1 text-emerald-400"></i>{{ nombre }}
        </span>
        <button
          @click="cerrarSesion"
          class="flex items-center gap-2 text-slate-400 hover:text-red-400 text-sm font-medium transition"
        >
          <i class="fa-solid fa-right-from-bracket"></i>
          <span class="hidden sm:inline">Salir</span>
        </button>
      </div>
    </nav>

    <!-- Contenido -->
    <div class="flex-1 flex flex-col items-center justify-center px-4 py-12">

      <!-- Estado de carga -->
      <div v-if="cargando" class="text-center">
        <i class="fa-solid fa-spinner fa-spin text-emerald-400 text-3xl mb-4"></i>
        <p class="text-slate-400">Cargando tus sistemas…</p>
      </div>

      <!-- Sin sistemas asignados -->
      <div v-else-if="sistemas.length === 0" class="text-center max-w-sm">
        <div class="w-16 h-16 rounded-2xl bg-slate-700 flex items-center justify-center mx-auto mb-4">
          <i class="fa-solid fa-box-open text-slate-400 text-2xl"></i>
        </div>
        <h2 class="text-white text-lg font-bold mb-2">Sin sistemas asignados</h2>
        <p class="text-slate-400 text-sm">Contacta al administrador para que te asigne acceso a los sistemas.</p>
      </div>

      <!-- Panel de selección -->
      <div v-else class="w-full max-w-lg">
        <h2 class="text-white text-2xl font-black text-center mb-2">
          Bienvenido, <span class="text-emerald-400">{{ nombre }}</span>
        </h2>
        <p class="text-slate-400 text-center text-sm mb-8">Selecciona el sistema al que deseas acceder</p>

        <!-- Combo de sistemas -->
        <div class="bg-slate-800 rounded-2xl border border-white/10 shadow-xl p-6">
          <label class="block text-slate-300 text-sm font-medium mb-3">
            <i class="fa-solid fa-cubes mr-2 text-emerald-400"></i>Tus sistemas disponibles
          </label>

          <select
            v-model="sistemaSeleccionado"
            class="w-full bg-slate-700 border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition cursor-pointer mb-2"
          >
            <option :value="null" disabled>-- Selecciona un sistema --</option>
            <option
              v-for="s in sistemas"
              :key="s.idSistema"
              :value="s"
            >
              {{ etiquetaCombo(s) }}
            </option>
          </select>

          <!-- Vista previa del sistema seleccionado -->
          <transition name="fade">
            <div v-if="sistemaSeleccionado">

              <!-- Tarjeta del sistema -->
              <div
                class="mt-4 rounded-xl p-4 flex items-center gap-4"
                :style="{ background: sistemaSeleccionado.gradiente || 'linear-gradient(135deg,#10b981,#059669)' }"
              >
                <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i :class="`${sistemaSeleccionado.icono} text-white text-xl`"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-white font-bold truncate">{{ sistemaSeleccionado.nombre }}</p>
                  <p class="text-white/70 text-xs truncate">{{ sistemaSeleccionado.descripcion }}</p>
                  <div class="flex flex-wrap gap-1 mt-1">
                    <span
                      class="inline-block text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="sistemaSeleccionado.estatus === 'A' ? 'bg-white/20 text-white' : 'bg-black/20 text-white/60'"
                    >
                      {{ sistemaSeleccionado.estatus === 'A' ? 'Activo' : 'Inactivo' }}
                    </span>
                    <!-- Badge de vigencia -->
                    <span
                      v-if="sistemaSeleccionado.estadoVigencia"
                      class="inline-block text-xs px-2 py-0.5 rounded-full font-semibold"
                      :class="badgeVigencia(sistemaSeleccionado.estadoVigencia)"
                    >
                      {{ textoVigenciaBadge(sistemaSeleccionado) }}
                    </span>
                    <!-- Badge usuario de equipo -->
                    <span
                      v-if="sistemaSeleccionado.esUsuarioEquipo"
                      class="inline-block text-xs px-2 py-0.5 rounded-full font-semibold bg-white/20 text-white"
                    >
                      <i class="fa-solid fa-users mr-1"></i>Usuario de equipo
                    </span>
                  </div>
                </div>
              </div>

              <!-- Alerta: VENCIDO -->
              <div
                v-if="sistemaSeleccionado.estadoVigencia === 'vencido'"
                class="mt-3 flex items-start gap-3 bg-red-500/15 border border-red-500/40 rounded-xl px-4 py-3"
              >
                <i class="fa-solid fa-circle-xmark text-red-400 mt-0.5 flex-shrink-0"></i>
                <div>
                  <p class="text-red-300 text-sm font-semibold">Suscripción vencida</p>
                  <p class="text-red-400/80 text-xs mt-0.5">
                    Tu acceso venció el <strong>{{ formatFecha(sistemaSeleccionado.fechaVencimiento) }}</strong>.
                    Realiza tu pago para reactivarlo.
                  </p>
                </div>
              </div>

              <!-- Alerta: PRÓXIMO A VENCER -->
              <div
                v-else-if="sistemaSeleccionado.estadoVigencia === 'proximo'"
                class="mt-3 flex items-start gap-3 bg-amber-500/15 border border-amber-500/40 rounded-xl px-4 py-3"
              >
                <i class="fa-solid fa-triangle-exclamation text-amber-400 mt-0.5 flex-shrink-0"></i>
                <div>
                  <p class="text-amber-300 text-sm font-semibold">Próximo a vencer</p>
                  <p class="text-amber-400/80 text-xs mt-0.5">
                    Tu suscripción vence en
                    <strong>{{ sistemaSeleccionado.diasRestantes }} día{{ sistemaSeleccionado.diasRestantes === 1 ? '' : 's' }}</strong>
                    ({{ formatFecha(sistemaSeleccionado.fechaVencimiento) }}).
                    Renueva para evitar interrupciones.
                  </p>
                </div>
              </div>

            </div>
          </transition>

          <!-- Botón entrar -->
          <button
            :disabled="!sistemaSeleccionado || sistemaSeleccionado.estadoVigencia === 'vencido'"
            @click="entrar"
            class="mt-5 w-full font-semibold py-3 rounded-xl shadow-lg transition-all duration-200 hover:scale-[1.02]"
            :class="botonClass"
          >
            <template v-if="sistemaSeleccionado && sistemaSeleccionado.estadoVigencia === 'vencido'">
              <i class="fa-solid fa-lock mr-2"></i>Acceso bloqueado — realiza tu pago
            </template>
            <template v-else>
              <i class="fa-solid fa-arrow-right-to-bracket mr-2"></i>Entrar al sistema
            </template>
          </button>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import authService from '@/services/auth.service'

export default {
  name: 'ClientePanelView',
  data () {
    return {
      sistemas:            [],
      sistemaSeleccionado: null,
      cargando:            true,
      nombre:              localStorage.getItem('nombre') || 'Cliente'
    }
  },
  computed: {
    botonClass () {
      if (!this.sistemaSeleccionado || this.sistemaSeleccionado.estadoVigencia === 'vencido') {
        return 'bg-slate-700 text-slate-400 cursor-not-allowed opacity-60'
      }
      return 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white shadow-emerald-500/30'
    }
  },
  async created () {
    const idUsuario = parseInt(localStorage.getItem('idUsuario'))
    if (!idUsuario) {
      this.$router.push({ name: 'Login' })
      return
    }
    try {
      const { data } = await authService.sistemasPorUsuario(idUsuario)
      if (data.resultado) {
        this.sistemas = (data.sistemas || []).filter(s =>
          s.estatus === 'A' &&
          s.estadoVigencia !== 'no_asignado'
        )
        // Si solo hay 1 sistema, seleccionarlo y entrar automáticamente
        // (excepto si se acaba de hacer logout desde un sistema externo)
        const params = new URLSearchParams(window.location.search)
        const esLogout = params.get('logout') === '1' || this.$route.query.logout === '1'
        if (this.sistemas.length === 1 && !esLogout) {
          this.sistemaSeleccionado = this.sistemas[0]
          this.$nextTick(() => this.entrar())
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      this.cargando = false
    }
  },
  methods: {
    // ── Etiqueta en el combo ──────────────────────────────────────────
    etiquetaCombo (s) {
      const iconos = { activo: '✅', proximo: '⚠️', vencido: '🔒', sin_fecha: '❓' }
      const icono  = iconos[s.estadoVigencia] || ''
      let label    = `${icono} ${s.nombre}`
      if (s.estatus === 'I') label += ' (Inactivo)'
      return label
    },

    // ── Badge de vigencia ─────────────────────────────────────────────
    badgeVigencia (estado) {
      return {
        activo:    'bg-emerald-500/30 text-emerald-300',
        proximo:   'bg-amber-500/30 text-amber-300',
        vencido:   'bg-red-500/30 text-red-300',
        sin_fecha: 'bg-slate-600/40 text-slate-400'
      }[estado] || ''
    },

    textoVigenciaBadge (s) {
      switch (s.estadoVigencia) {
        case 'activo':    return `Vigente · ${s.diasRestantes}d restantes`
        case 'proximo':   return `⚠ Vence en ${s.diasRestantes}d`
        case 'vencido':   return '🔒 Vencido'
        case 'sin_fecha': return 'Sin fecha'
        default:          return ''
      }
    },

    // ── Formato fecha ─────────────────────────────────────────────────
    formatFecha (iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    },

    // ── Entrar al sistema ─────────────────────────────────────────────
    async entrar () {
      if (!this.sistemaSeleccionado) return
      if (this.sistemaSeleccionado.estadoVigencia === 'vencido') return

      const url = (this.sistemaSeleccionado.url || '').trim()
      if (!url) {
        this.$store.dispatch('mostrarToast', {
          mensaje: `El sistema "${this.sistemaSeleccionado.nombre}" aún no tiene URL configurada.`,
          tipo:    'warning'
        })
        return
      }

      // Obtener SSO token de vida corta
      try {
        const { data } = await authService.generarSsoToken()
        if (!data.resultado) {
          this.$store.dispatch('mostrarToast', { mensaje: 'No se pudo generar el acceso.', tipo: 'error' })
          return
        }
        // Construir URL de destino — origen + path sin barra final
        const baseUrl    = (() => { try { const u = new URL(url); return u.origin + u.pathname.replace(/\/+$/, '') } catch { return url.replace(/\/+$/, '') } })()
        const returnUrl  = encodeURIComponent(window.location.origin + '/login')
        const ssoToken   = encodeURIComponent(data.ssoToken)
        const mavToken   = encodeURIComponent(localStorage.getItem('token') || '')
        const idSistema  = this.sistemaSeleccionado.idSistema
        const destino    = `${baseUrl}/sso?token=${ssoToken}&return=${returnUrl}&mavToken=${mavToken}&idSistema=${idSistema}`
        window.location.href = destino
      } catch {
        this.$store.dispatch('mostrarToast', { mensaje: 'Error al iniciar sesión en el sistema.', tipo: 'error' })
      }
    },

    cerrarSesion () {
      this.$store.dispatch('cerrarSesion')
      this.$router.push({ name: 'Login' })
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .25s, transform .25s; }
.fade-enter, .fade-leave-to           { opacity: 0; transform: translateY(-6px); }
</style>

